import { useTranslation } from "react-i18next"
import {
  ArrowRight,
  Brain,
  EyedropperSample,
  FileText,
  Plus,
  Prescription,
  SuitcaseSimple,
  TestTube,
} from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { FaroInsightsWrapper } from "@/grafana-faro"
import {
  isMeasMedgateConnectAppointment,
  isMedgateConnect,
} from "@/modules/patients/agenda/teleconsultation/booking/constants/medgate-connect"
import { ConsultationReportDrawer } from "@/modules/patients/detail/consultation-report/consultation-report-drawer"
import { useConsultationReportStoreContext } from "@/modules/patients/detail/consultation-report/store/useConsultationReportStore"
import { ChGeneralAttestationDrawer } from "@/modules/patients/detail/general-attestation/ch/ch-general-attestation-drawer"
import { DeGeneralAttestationDrawer } from "@/modules/patients/detail/general-attestation/de/de-general-attestation-drawer"
import { useGeneralAttestationContext } from "@/modules/patients/detail/general-attestation/store/useGeneralAttestationStore"
import { ChLaboratoryPrescriptionDrawer } from "@/modules/patients/detail/laboratory-prescription/ch/ch-laboratory-prescription-drawer"
import { ChLegalGuardianAttestationDrawer } from "@/modules/patients/detail/legal-guardian-attestation/ch/ch-legal-guardian-attestation-drawer"
import { DeLegalGuardianAttestationDrawer } from "@/modules/patients/detail/legal-guardian-attestation/de/de-legal-guardian-attestation-drawer"
import { useLegalGuardianAttestationDrawerContext } from "@/modules/patients/detail/legal-guardian-attestation/store/useLegalGuardianAttestationDrawerStore"
import { ChMeasLaboratoryPrescriptionDrawer } from "@/modules/patients/detail/meas-laboratory-prescription/ch/ch-meas-laboratory-prescription-drawer"
import { ChMeasRadiologyPrescriptionDrawer } from "@/modules/patients/detail/meas-radiology-prescription/ch/ch-meas-radiology-prescription-drawer"
import { ChNeuropsychologicDiagnosticPrescriptionDrawer } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/ch-neuropsychologic-diagnostic-prescription-drawer"
import { ChPrescriptionDrawer } from "@/modules/patients/detail/prescription/ch/ch-prescription-drawer"
import { useChPrescriptionStoreContext } from "@/modules/patients/detail/prescription/ch/store/usePrescriptionStore"
import { DePrescriptionDrawer } from "@/modules/patients/detail/prescription/de/de-prescription-drawer"
import { useDePrescriptionStoreContext } from "@/modules/patients/detail/prescription/de/store/usePrescriptionStore"
import { DropdownMenuSelection } from "@/modules/patients/detail/progress-note/components/dropdown-menu-selection"
import { RadiologyIcon } from "@/modules/patients/detail/progress-note/documents/meas-radiology-prescription-document"
import { NutritionIcon } from "@/modules/patients/detail/progress-note/documents/nutritional-counselling-document"
import { PsychologicalPsychotherapyIcon } from "@/modules/patients/detail/progress-note/documents/psychological-psychotherapy"
import { PhysicalTherapyIcon } from "@/modules/patients/detail/progress-note/documents/therapy-prescription-document"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { ChReferralDrawer } from "@/modules/patients/detail/referral/ch/ch-referral-drawer.tsx"
import { DeReferralDrawer } from "@/modules/patients/detail/referral/de/de-referral-drawer.tsx"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { ChSickLeaveCertificateDrawer } from "@/modules/patients/detail/sick-leave-certificate/ch/ch-sick-leave-certificate-drawer"
import { DeSickLeaveCertificateDrawer } from "@/modules/patients/detail/sick-leave-certificate/de/de-sick-leave-certificate-drawer"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { ChNutritionalCounsellingDrawer } from "@/modules/patients/detail/therapy-prescription/ch/ch-nutritional-counselling-drawer"
import { ChPhysiotherapyDrawer } from "@/modules/patients/detail/therapy-prescription/ch/ch-physiotherapy-drawer"
import { ChPsychologicalTherapyDrawer } from "@/modules/patients/detail/therapy-prescription/ch/ch-psychological-psychotherapy-drawer"
import { DeTherapyPrescriptionDrawer } from "@/modules/patients/detail/therapy-prescription/de/de-therapy-prescription-drawer"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { chProgressNoteFragment$data } from "@/modules/patients/services/ch/__generated__/chProgressNoteFragment.graphql"
import { deProgressNoteFragment$data } from "@/modules/patients/services/de/__generated__/deProgressNoteFragment.graphql"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import {
  CH_LEGAL_GUARDIAN_LIMITED_AGE,
  DE_LEGAL_GUARDIAN_LIMITED_AGE,
} from "@/modules/patients/types/ProgressNoteDocument"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

type MenuItem = {
  isActive: boolean
  isShown: boolean
  testId: string
  icon: React.JSX.Element
  label: string
  onClick: () => void
}

const DocumentsMenuList = ({
  progressNote,
  diagnosisCode,
}: {
  progressNote: ProgressNoteData
  diagnosisCode: string
}) => {
  const { t } = useTranslation()

  const patientAge = usePatientFileContext((state) => state.patientFileInfo?.age?.years)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const appointmentId = useAppointmentParam()
  const [patientData] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const patient = patientData.patientById
  const doctorNetworks = patient?.__typename === "ch_Patient" ? patient.doctorNetworks : []
  const confirm = useConfirmStore((state) => state.confirm)
  const setConsultationReportRef = useConsultationReportStoreContext((state) => state.setConsultationReportRef)
  const resetChPrescriptionStore = useChPrescriptionStoreContext((state) => state.reset)
  const resetDePrescriptionStore = useDePrescriptionStoreContext((state) => state.reset)

  const setGeneralAttestationProgressNoteId = useGeneralAttestationContext((state) => state.setProgressNoteId)

  const setLegalGuardianAttestationProgressNoteId = useLegalGuardianAttestationDrawerContext(
    (state) => state.setProgressNoteId,
  )

  const setReferralProgressNoteId = useReferralStoreContext((state) => state.setProgressNoteId)

  const { id: progressNoteId, appointment, followUpAppointment, __typename } = progressNote

  const getDeMenuItems: (deProgressNote: deProgressNoteFragment$data) => MenuItem[] = (deProgressNote) => {
    return [
      {
        isActive: true,
        isShown: !deProgressNote.prescription,
        icon: <Prescription className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.prescription",
        testId: "prescription-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_prescription_start")
          openDrawer(() => <DePrescriptionDrawer progressNoteId={progressNoteId} />, "PrescriptionDrawer", {
            onBeforeOpen() {
              resetDePrescriptionStore()
            },
          })
        },
      },
      {
        isActive: patientAge !== undefined && patientAge < DE_LEGAL_GUARDIAN_LIMITED_AGE,
        isShown: !deProgressNote.attestationGuardian,
        icon: <FileText className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.attestationForLegalGuardian",
        testId: "attestation-for-legal-guardian-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_legalGuardian_start")
          setLegalGuardianAttestationProgressNoteId(progressNoteId)
          openDrawer(() => <DeLegalGuardianAttestationDrawer />, "LegalGuardianAttestationDrawer")
        },
      },
      {
        isActive: true,
        isShown: !deProgressNote.sickLeave,
        icon: <SuitcaseSimple className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.sickLeaveCertificate.de",
        testId: "sick-leave-certificate-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_sickLeave_start")
          openDrawer(
            () => <DeSickLeaveCertificateDrawer progressNoteId={progressNoteId} />,
            "SickLeaveCertificateDrawer",
          )
        },
      },
      {
        isActive: true,
        isShown: !deProgressNote.referralLetter,
        icon: <ArrowRight className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.referral",
        testId: "referral-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_referral_start")
          setReferralProgressNoteId(progressNoteId)
          openDrawer(() => <DeReferralDrawer />, "ReferralDrawer")
        },
      },
      {
        isActive: true,
        isShown: !deProgressNote.generalAttestation,
        icon: <FileText className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.generalAttestation",
        testId: "general-attestation-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_generalAttestation_start")
          setGeneralAttestationProgressNoteId(progressNoteId)
          openDrawer(() => <DeGeneralAttestationDrawer />, "GeneralAttestationDrawer")
        },
      },
      {
        isActive: true,
        isShown: !deProgressNote.therapyPrescription,
        icon: <PhysicalTherapyIcon />,
        label: `episodeOfCare.progressNote.deTherapyPrescription`,
        testId: "therapy-prescription-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_therapy_start")
          openDrawer(() => <DeTherapyPrescriptionDrawer progressNoteId={progressNoteId} />, "TherapyPrescriptionDrawer")
        },
      },
    ]
  }

  const getChMenuItems: (chProgressNote: chProgressNoteFragment$data) => MenuItem[] = (chProgressNote) => {
    const isMeasDocumentsActive =
      appointment.access === "BOOK_DOC_AT_PHARMACY" ||
      (appointment.appointmentType === "FOLLOW_UP" && isMedgateConnect(appointment.service)) ||
      isMeasMedgateConnectAppointment(appointment) ||
      isMeasMedgateConnectAppointment(chProgressNote.followUpAppointment)

    return [
      {
        isActive: true,
        isShown: !chProgressNote.prescription,
        icon: <Prescription className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.prescription",
        testId: "prescription-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_prescription_start")
          openDrawer(
            () => <ChPrescriptionDrawer progressNoteId={progressNoteId} diagnosisCode={diagnosisCode} />,
            "PrescriptionDrawer",
            {
              onBeforeOpen() {
                resetChPrescriptionStore()
              },
            },
          )
        },
      },
      {
        isActive: patientAge !== undefined && patientAge < CH_LEGAL_GUARDIAN_LIMITED_AGE,
        isShown: !chProgressNote.attestationGuardian,
        icon: <FileText className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.attestationForLegalGuardian",
        testId: "attestation-for-legal-guardian-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_legalGuardian_start")
          setLegalGuardianAttestationProgressNoteId(progressNoteId)
          openDrawer(() => <ChLegalGuardianAttestationDrawer />, "LegalGuardianAttestationDrawer")
        },
      },
      {
        isActive: true,
        isShown: !chProgressNote.generalAttestation,
        icon: <FileText className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.generalAttestation",
        testId: "general-attestation-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_generalAttestation_start")
          setGeneralAttestationProgressNoteId(progressNoteId)
          openDrawer(() => <ChGeneralAttestationDrawer />, "GeneralAttestationDrawer")
        },
      },
      {
        isActive: true,
        isShown: !chProgressNote.sickLeave,
        icon: <SuitcaseSimple className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.sickLeaveCertificate.ch",
        testId: "sick-leave-certificate-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_sickLeave_start")
          openDrawer(
            () => <ChSickLeaveCertificateDrawer progressNoteId={progressNoteId} />,
            "SickLeaveCertificateDrawer",
          )
        },
      },
      {
        isActive: true,
        isShown: !chProgressNote.referralLetter,
        icon: <ArrowRight className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.referral",
        testId: "referral-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_referral_start")
          setReferralProgressNoteId(progressNoteId)
          openDrawer(() => <ChReferralDrawer />, "ReferralDrawer")
        },
      },
      {
        isActive: true,
        isShown: !chProgressNote.ch_nutritionalPrescription,
        icon: <NutritionIcon />,
        label: "episodeOfCare.progressNote.therapyPrescriptionNutritionalCounselling",
        testId: "therapy-prescription-nutritional-counselling-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_nutrition_start")
          openDrawer(
            () => <ChNutritionalCounsellingDrawer progressNoteId={progressNoteId} />,
            "NutritionalCounsellingDrawer",
          )
        },
      },
      {
        isActive: true,
        isShown: !chProgressNote.physiotherapy,
        icon: <PhysicalTherapyIcon />,
        label: "patient.therapyPrescriptionPhysiotherapy.therapyPrescriptionPhysiotherapy",
        testId: "physiotherapy-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_physiotherapy_start")
          openDrawer(() => <ChPhysiotherapyDrawer progressNoteId={progressNoteId} />, "PhysiotherapyDrawer")
        },
      },
      {
        isActive: true,
        isShown: !chProgressNote.psychologicalPsychotherapy,
        icon: <PsychologicalPsychotherapyIcon />,
        label: "episodeOfCare.progressNote.therapyPrescriptionPsychologicalPsychotherapy",
        testId: "therapy-prescription-psychological-psychotherapy-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_psychotherapy_start")
          openDrawer(
            () => <ChPsychologicalTherapyDrawer progressNoteId={progressNoteId} />,
            "PsychologicalTherapyDrawer",
          )
        },
      },
      {
        isActive: isMeasDocumentsActive,
        isShown: !chProgressNote.measLaboratoryPrescription,
        icon: <TestTube className="text-on-surface-subdued" />,
        label: "patient.measLaboratoryPrescription.measLaboratoryPrescription",
        testId: "meas-laboratory-prescription-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_meas_lab_start")
          openDrawer(
            () => (
              <ChMeasLaboratoryPrescriptionDrawer
                progressNoteId={progressNoteId}
                fileUploadRequest={__typename === "ch_ProgressNote" ? followUpAppointment?.fileUploadRequest : null}
                medgateConnectOrder={__typename === "ch_ProgressNote" ? followUpAppointment?.medgateConnectOrder : null}
              />
            ),
            "MeasLaboratoryPrescriptionDrawer",
          )
        },
      },
      {
        isActive: isMeasDocumentsActive,
        isShown: !chProgressNote.ch_measRadiologyPrescription,
        icon: <RadiologyIcon />,
        label: "patient.measRadiologyPrescription.radiologyPrescriptionMEAS",
        testId: "radiology-prescription-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_meas_radio_start")
          openDrawer(
            () => (
              <ChMeasRadiologyPrescriptionDrawer
                progressNoteId={progressNoteId}
                fileUploadRequest={__typename === "ch_ProgressNote" ? followUpAppointment?.fileUploadRequest : null}
                medgateConnectOrder={__typename === "ch_ProgressNote" ? followUpAppointment?.medgateConnectOrder : null}
              />
            ),
            "MeasRadiologyPrescriptionDrawer",
          )
        },
      },
      {
        isActive: true,
        isShown: !chProgressNote.ch_neuropsychologicDiagnosticPrescription,
        icon: <Brain className="text-on-surface-subdued" />,
        label: "patient.neuropsychologicDiagnosticPrescription.neuropsychologicDiagnosticPrescription",
        testId: "neuropsychologic-diagnostic-prescription-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_neuroPsychology_start")
          openDrawer(
            () => <ChNeuropsychologicDiagnosticPrescriptionDrawer progressNoteId={progressNoteId} />,
            "NeuropsychologicDiagnosticPrescriptionDrawer",
          )
        },
      },
      {
        isActive: true,
        isShown: !chProgressNote.ch_laboratoryPrescription,
        icon: <EyedropperSample className="text-on-surface-subdued" />,
        label: "patient.laboratoryPrescription.prescriptionForMedgateConnect",
        testId: "laboratory-prescription-menu",
        onClick: () => {
          FaroInsightsWrapper.pushEvent("documents_laboratory_prescription_start")
          openDrawer(
            () => (
              <ChLaboratoryPrescriptionDrawer
                progressNoteId={progressNoteId}
                medgateConnectOrder={__typename === "ch_ProgressNote" ? followUpAppointment?.medgateConnectOrder : null}
              />
            ),
            "LaboratoryPrescriptionDrawer",
          )
        },
      },
      {
        isActive: true,
        isShown: !chProgressNote.ch_consultationReport,
        icon: <FileText className="text-on-surface-subdued" />,
        label: "episodeOfCare.progressNote.consultationReport",
        testId: "consultation-report-menu",
        onClick: () => {
          if (doctorNetworks.length === 0) {
            confirm({
              title: "patient.consultationReport.noPersonalCareProvider.title",
              content: "patient.consultationReport.noPersonalCareProvider.description",
              cancelText: "common.button.close",
              isShowConfirmButton: false,
            })
            return
          }
          FaroInsightsWrapper.pushEvent("documents_consultation_report_start")
          setConsultationReportRef(null)
          openDrawer(() => <ConsultationReportDrawer progressNoteId={progressNoteId} />, "ConsultationReportDrawer")
        },
      },
    ]
  }

  const isEveryMenuHasDocument = (menuItems: MenuItem[]) => menuItems.every((m) => !m.isShown)

  const populateMenuItems = (dataSource: typeof progressNote) => {
    let result: MenuItem[]
    switch (dataSource.__typename) {
      case "ch_ProgressNote":
        result = getChMenuItems(dataSource as chProgressNoteFragment$data)
        break
      case "de_ProgressNote":
        result = getDeMenuItems(dataSource as deProgressNoteFragment$data)
        break
      default:
        throw new Error("Unsupported progress note type")
    }

    return result.filter((m) => m.isActive)
  }

  const menuItems = populateMenuItems(progressNote)

  if (isEveryMenuHasDocument(menuItems)) {
    return (
      <DropdownMenuSelection data-testid="no-document-type-available-menu" disabled>
        {t("episodeOfCare.progressNote.noMoreDocumentTypeAvailable")}
      </DropdownMenuSelection>
    )
  }

  return (
    <>
      {menuItems
        .filter((m) => m.isShown)
        .map((m) => (
          <DropdownMenuSelection key={m.label} data-testid={m.testId} onClick={m.onClick}>
            {m.icon}
            {t(m.label)}
          </DropdownMenuSelection>
        ))}
    </>
  )
}

const DocumentsMenu = ({ progressNote, diagnosisCode }: { progressNote: ProgressNoteData; diagnosisCode: string }) => {
  return (
    <div className="pr-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="documents-open-menu-button" variant="tertiary" className="h-10 w-10 p-2">
            <Plus className="text-on-surface-subdued" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="min-w-75 rounded-lg p-2"
          onCloseAutoFocus={(e) => {
            e.preventDefault()
          }}>
          <DocumentsMenuList progressNote={progressNote} diagnosisCode={diagnosisCode} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export { DocumentsMenu }
